import type { FirmStandards } from "@contract/standards/standards";
import { Anchor, Group, Text } from "@mantine/core";
import { IconAlertCircle, IconCircleCheck } from "@tabler/icons-react";
import * as React from "react";
import { Link } from "react-router";
import { fetchFirmStandards } from "../../api/standards";
import { DetailRow, InlineEdit } from "../../ui/DetailRow";
import { SectionCard } from "../../ui/SectionCard";
import { PANEL_PALETTES } from "../../ui/tokens";
import { brandStatus } from "../homeModel";

/** Each row, and the Brand Configuration tab its Edit opens. */
const ROWS = [
  { key: "colors", label: "Colors", tab: "colors" },
  { key: "typography", label: "Typography", tab: "colors" },
  { key: "language", label: "Language", tab: "language" },
  { key: "labels", label: "Labels", tab: "language" },
] as const;

/**
 * Home's Brand Configuration card (A8, Dan's frame 15187:6107): whether the firm has set its
 * colors, typography, language and labels. A Not Set row links to its Brand Configuration tab
 * for those who can edit standards (Dan 2026-10-02: Edit on Not Set only). Renders nothing
 * until the record arrives, and nothing if the read fails.
 */
export function BrandStatusCard({ canEdit, load = fetchFirmStandards }: { canEdit: boolean; load?: () => Promise<{ standards: FirmStandards }> }) {
  const [standards, setStandards] = React.useState<FirmStandards | null>(null);
  React.useEffect(() => {
    let alive = true;
    load()
      .then((r) => alive && setStandards(r.standards))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  if (!standards) return null;
  const status = brandStatus(standards);
  return (
    <SectionCard title="Brand Configuration" gap={8}>
      {ROWS.map((row) => (
        <DetailRow key={row.key} label={row.label}>
          {status[row.key] ? (
            <Group gap={5} wrap="nowrap">
              <IconCircleCheck size={16} stroke={1.6} color={PANEL_PALETTES.green[6]} aria-hidden />
              <Text c={PANEL_PALETTES.green[6]} lh={1.25}>
                Set
              </Text>
            </Group>
          ) : (
            <Group gap={5} wrap="nowrap">
              <IconAlertCircle size={16} stroke={1.6} color={PANEL_PALETTES.red[7]} aria-hidden />
              <Text c={PANEL_PALETTES.red[7]} lh={1.25}>
                Not Set
                {canEdit ? (
                  <InlineEdit>
                    <Anchor component={Link} to={`/standards?tab=${row.tab}`} fw={400} inherit aria-label={`Edit ${row.label.toLowerCase()}`}>
                      (Edit)
                    </Anchor>
                  </InlineEdit>
                ) : null}
              </Text>
            </Group>
          )}
        </DetailRow>
      ))}
    </SectionCard>
  );
}
